import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WandSparkles } from 'lucide-react';
import { buildErrorMessage } from '../../api/authorWithAiBuildErrors';
import { useAuthorWithAiAvailability, useCreateBuild } from '../../api/authorWithAiQueries';
import type { CenterstageVideo } from '../../api/centerstageApi';
import { useVideoDetail } from '../../api/centerstageQueries';
import { AdminStatusMessage } from '../../admin-ui';
import { CenterstageVideoDialog } from '../../components/centerstage/CenterstageVideoDialog';
import { CENTERSTAGE_COPY as COPY } from '../../components/centerstage/centerstageCopy';
import type { CenterstageStyles } from '../../components/centerstage/centerstageStyles';
import type { CenterstageFilterPatch } from './useCenterstageFilters';

export interface CenterstageVideoModalProps {
  styles: CenterstageStyles;
  /** The card's row; null keeps the dialog closed. */
  video: CenterstageVideo | null;
  /** The content context the build is created in (null for Standard). */
  accountId: number | null;
  onClose: () => void;
  /** Sets one filter from a Type, Setting or concept link; the page closes the dialog. */
  onFilter: (patch: CenterstageFilterPatch) => void;
}

/**
 * One video in the shared Centerstage dialog, plus the page's Type, Setting
 * and concept links (each sets a filter) and "Build a lesson around this
 * video", which starts an Author with AI build from the video (the same state
 * a "Lead with a video scenario" pick reaches).
 */
export function CenterstageVideoModal({ styles, video, accountId, onClose, onFilter }: CenterstageVideoModalProps) {
  const navigate = useNavigate();
  const availability = useAuthorWithAiAvailability();
  const detailQuery = useVideoDetail(video?.id ?? null);
  const create = useCreateBuild();
  const creating = useRef(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  useEffect(() => {
    setBuildError(null);
  }, [video?.id]);

  const detail = detailQuery.data ?? null;
  const missing = detailQuery.isSuccess && detail === null;
  const buildBlocked = missing || create.isPending;

  const build = () => {
    if (!video || creating.current || buildBlocked) return;
    creating.current = true;
    setBuildError(null);
    create.mutate({ accountId, source: { kind: 'video', libraryId: video.id } }, {
      onSettled: () => { creating.current = false; },
      onSuccess: (response) => navigate(`/author-with-ai/builds/${response.buildId}`),
      onError: (error) => setBuildError(buildErrorMessage(error, COPY.buildError)),
    });
  };

  return (
    <CenterstageVideoDialog
      styles={styles}
      video={video}
      onClose={onClose}
      footer={availability.status === 'available' ? (
        <>
          {buildError ? (
            <AdminStatusMessage tone="error" style={styles.footerError}>{buildError}</AdminStatusMessage>
          ) : null}
          <button
            type="button"
            onClick={build}
            disabled={buildBlocked}
            style={{ ...styles.buildButton, ...(buildBlocked ? styles.buttonDisabled : {}) }}
          >
            {/* The Author with AI glyph, as in the nav rail and the Author with AI ask field. */}
            <WandSparkles size="1em" strokeWidth={1.75} aria-hidden="true" />
            {create.isPending ? COPY.buildingLesson : COPY.buildLesson}
          </button>
        </>
      ) : null}
    >
      {detail?.type || detail?.setting ? (
        <dl style={styles.definitionGrid}>
          {detail.type ? (
            <div style={styles.definitionItem}>
              <dt style={styles.metaLabel}>{COPY.type}</dt>
              <dd style={{ margin: 0 }}>
                <button type="button" onClick={() => onFilter({ type: detail.type!.value })} style={styles.linkButton}>
                  {detail.type.label}
                </button>
              </dd>
            </div>
          ) : null}
          {detail.setting ? (
            <div style={styles.definitionItem}>
              <dt style={styles.metaLabel}>{COPY.setting}</dt>
              <dd style={{ margin: 0 }}>
                <button type="button" onClick={() => onFilter({ setting: detail.setting!.value })} style={styles.linkButton}>
                  {detail.setting.label}
                </button>
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {detail?.concepts && detail.concepts.length > 0 ? (
        <div style={styles.definitionItem}>
          <h3 style={styles.metaLabel}>{COPY.concepts}</h3>
          <ul style={styles.conceptLinks}>
            {detail.concepts.map((concept, index) => (
              <Fragment key={concept.id}>
                {index > 0 ? <li aria-hidden="true" style={styles.conceptDot}>·</li> : null}
                <li>
                  <button type="button" onClick={() => onFilter({ conceptId: concept.id })} style={styles.linkButton}>
                    {concept.name}
                  </button>
                </li>
              </Fragment>
            ))}
          </ul>
        </div>
      ) : null}
    </CenterstageVideoDialog>
  );
}
